import { useState, useRef, useEffect } from 'react';
import { MoreVertical } from 'lucide-react';

/**
 * Menu vedlejších operací u hlavičky detailu (tři tečky vedle Uložit).
 *
 * Hlavička nesla archivační přepínač i mazání jako plnohodnotná tlačítka vedle
 * Uložit — tři akce se stejnou vahou, přestože se maže jednou za čas a ukládá
 * pořád. Vedlejší operace jdou sem, primární tlačítko zůstane samo.
 *
 * @param {Array<{id, label, icon, onClick, danger?, hidden?}>} polozky
 */
export default function AkceMenu({ polozky = [], isDark, mutedColor, glassCard, title = 'Další akce' }) {
    const [otevreno, setOtevreno] = useState(false);
    const obal = useRef(null);

    // Zavřít kliknutím mimo i Escapem — menu drží fokus jen po dobu volby.
    useEffect(() => {
        if (!otevreno) return;
        const mimo = (e) => { if (obal.current && !obal.current.contains(e.target)) setOtevreno(false); };
        const klavesa = (e) => { if (e.key === 'Escape') setOtevreno(false); };
        document.addEventListener('mousedown', mimo);
        document.addEventListener('keydown', klavesa);
        return () => { document.removeEventListener('mousedown', mimo); document.removeEventListener('keydown', klavesa); };
    }, [otevreno]);

    const viditelne = polozky.filter(p => !p.hidden);
    if (viditelne.length === 0) return null;

    // „Vtlačení do povrchu" = tmavší kolečko + vnitřní stín, ne obrys.
    const prohluben = isDark ? 'rgba(0,0,0,0.28)' : 'rgba(0,0,0,0.06)';
    const vnitrniStin = isDark
        ? 'inset 0 1px 2px rgba(0,0,0,0.45), inset 0 -1px 0 rgba(255,255,255,0.04)'
        : 'inset 0 1px 2px rgba(0,0,0,0.10)';

    return (
        <div ref={obal} style={{ position: 'relative', flexShrink: 0 }}>
            {/* Bez rámečku — tři tečky vtlačené do povrchu, ne další tlačítko
                vedle Uložit. Prohlubeň se rozsvítí až po najetí nebo při otevření. */}
            <button onClick={() => setOtevreno(o => !o)} title={title} aria-haspopup="menu" aria-expanded={otevreno}
                className="flex items-center justify-center rounded-full"
                style={{
                    width: 30, height: 30, border: 'none', cursor: 'pointer',
                    color: otevreno ? 'var(--text-primary)' : mutedColor,
                    background: otevreno ? prohluben : 'transparent',
                    boxShadow: otevreno ? vnitrniStin : 'none',
                    transition: 'background 0.15s, box-shadow 0.15s, color 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = prohluben; e.currentTarget.style.boxShadow = vnitrniStin; e.currentTarget.style.color = 'var(--text-primary)'; }}
                onMouseLeave={e => {
                    if (otevreno) return;
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.color = mutedColor;
                }}>
                <MoreVertical size={16} />
            </button>

            {otevreno && (
                <div role="menu"
                    style={{
                        ...glassCard, position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 60,
                        minWidth: 190, borderRadius: 9, overflow: 'hidden', padding: 4,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                    }}>
                    {viditelne.map(p => {
                        const Icon = p.icon;
                        return (
                            <button key={p.id} role="menuitem"
                                onClick={() => { setOtevreno(false); p.onClick?.(); }}
                                className="flex items-center gap-2 w-full text-left text-xs font-medium rounded-lg px-2.5 py-2"
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: p.danger ? '#EF4444' : 'var(--text-primary)',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = p.danger ? 'rgba(239,68,68,0.10)' : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'); }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                                {Icon && <Icon size={14} style={{ flexShrink: 0 }} />}
                                {p.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
